import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  Spinner,
  Alert,
  Table
} from 'reactstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import dealService from '../../../../service/dealService.ts';

const HistoryAction = ({ isOpen, toggle, deal }) => {
  const { getToken } = useAuth();
  const [auditEvents, setAuditEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    if (isOpen && deal?.privateId) {
      loadAuditEvents();
    }
  }, [isOpen, deal?.privateId]);

  const loadAuditEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      const response = await dealService.getDealAuditEvents(token, deal.privateId);
      
      // Handle both array and object responses
      const events = Array.isArray(response.data) ? response.data : [response.data];
      setAuditEvents(events);
    } catch (err) {
      console.error('Error loading audit events:', err);
      setError('Failed to load audit events');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${month}/${day}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedEvent(null);
  };

  const getEventColor = (event) => {
    const eventColors = {
      'DEAL_CREATED': 'success',
      'DEAL_UPDATED': 'info',
      'DEAL_DELETED': 'danger',
      'STATUS_CHANGED': 'warning',
      'OFFER_CREATED': 'primary',
      'OFFER_SELECTED': 'success'
    };
    return eventColors[event] || 'secondary';
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="xl"
        style={{ maxWidth: '1200px' }}
      >
        <ModalHeader toggle={toggle}>
          <i className="ni ni-archive-2 mr-2" />
          Record History
        </ModalHeader>
        <ModalBody style={{ padding: '1.5rem' }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-2">Loading audit events...</p>
            </div>
          ) : error ? (
            <Alert color="danger">
              <i className="ni ni-fat-remove mr-2" />
              {error}
            </Alert>
          ) : auditEvents.length === 0 ? (
            <div className="text-center py-5">
              <i className="ni ni-collection text-muted mb-3" style={{ fontSize: '3rem' }} />
              <Alert color="info" className="mb-0">
                <i className="ni ni-notification-70 mr-2" />
                <strong>No History</strong>
                <p className="mb-0 mt-2">
                  No audit events found for this deal.
                </p>
              </Alert>
            </div>
          ) : (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table className="align-items-center table-flush" hover responsive>
                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th scope="col">Event</th>
                    <th scope="col">Description</th>
                    <th scope="col">Origin</th>
                    <th scope="col">User</th>
                    <th scope="col">Date</th>
                    <th scope="col">Ref ID</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEvents.map((event, index) => (
                    <tr 
                      key={event.id || index}
                      onClick={() => handleEventClick(event)}
                      style={{ cursor: 'pointer' }}
                      className="table-row-hover"
                    >
                      <td>
                        <Badge 
                          color={getEventColor(event.event)} 
                          pill 
                          style={{ fontSize: '0.65rem', padding: '0.35em 0.65em', fontWeight: '500' }}
                        >
                          {event.event ? truncateText(event.event.replace(/_/g, ' '), 20) : 'N/A'}
                        </Badge>
                      </td>
                      <td>
                        <span className="text-muted">
                          {truncateText(event.description, 50)}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm">
                          {event.origin || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm">
                          {truncateText(event.userName, 20)}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm">
                          {formatDate(event.date)}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm">
                          {event.referenceId || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        toggle={handleCloseDetailModal}
        size="lg"
      >
        <ModalHeader toggle={handleCloseDetailModal}>
          <i className="ni ni-archive-2 mr-2" />
          Event Details
        </ModalHeader>
        <ModalBody style={{ padding: '1.5rem' }}>
          {selectedEvent && (
            <>
              <div className="mb-3">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Event:</strong>
                    <div className="mt-1">
                      <Badge 
                        color={getEventColor(selectedEvent.event)} 
                        pill 
                        style={{ fontSize: '0.75rem', padding: '0.4em 0.8em', fontWeight: '500' }}
                      >
                        {selectedEvent.event ? selectedEvent.event.replace(/_/g, ' ') : 'N/A'}
                      </Badge>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <strong>Reference ID:</strong>
                    <div className="mt-1">
                      {selectedEvent.referenceId || 'N/A'}
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Origin:</strong>
                    <div className="mt-1">
                      {selectedEvent.origin || 'N/A'}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <strong>User:</strong>
                    <div className="mt-1">
                      {selectedEvent.userName || 'N/A'}
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-12">
                    <strong>Date:</strong>
                    <div className="mt-1">
                      {formatDate(selectedEvent.date)}
                    </div>
                  </div>
                </div>
              </div>
              <hr />
              <div className="mt-3">
                <strong className="mb-2 d-block">Description:</strong>
                <div 
                  style={{ 
                    border: '1px solid #dee2e6', 
                    borderRadius: '0.375rem', 
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                >
                  {selectedEvent.description || 'No description available'}
                </div>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default HistoryAction;

